import Quill from 'quill/core';
import React, { useMemo } from 'react';
import useAppSelector from '../../../app/redux/useAppSelector';
import convertSelectionToCodeEmbed from '../../code/convertSelectionToCodeEmbed';
import { useQuill } from '../../QuillContext';
import {
  boldIcon,
  codeIcon,
  italicIcon,
  linkIcon,
  strikethroughIcon,
} from '../editorIcons';
import ToolbarCommentButton from './buttons/comments/ToolbarCommentButton';
import ShortcutTooltip from './buttons/ShortcutTooltip';
import ToolbarButton, { ToolbarButtonGroup } from './buttons/ToolbarButton';
import ToolbarCommentButtonGroup from './buttons/ToolbarCommentButtonGroup';
import ToolbarEditModeOverflowMenuButton from './buttons/ToolbarEditModeOverflowMenuButton';
import ToolbarHighlightButton from './buttons/ToolbarHighlightButton';
import type { ToolbarObjectButtonOptions } from './buttons/ToolbarObjectButton';
import ToolbarObjectButton from './buttons/ToolbarObjectButton';

interface EditModeToolbarProps {
  multiline: boolean;
  inTitleline: boolean;
  closeToolbar: () => void;
  onLinkSelected: () => void;
  objectButtonOptions?: ToolbarObjectButtonOptions;
}

const EditModeToolbar = ({
  multiline,
  inTitleline,
  closeToolbar,
  onLinkSelected,
  objectButtonOptions,
}: EditModeToolbarProps) => {
  const {
    quill,
    selection: { formats },
  } = useQuill();

  const isSupportingCodeEmbed = useMemo(
    () => !!quill?.scroll.query('code-embed'),
    [quill],
  );
  const isSupportingInlineCode = useMemo(
    () => !!quill?.scroll.query('code'),
    [quill],
  );
  const isSupportingAnnotations = useMemo(
    () => !!quill?.scroll.query('annotation'),
    [quill],
  );
  const isSupportingComments = useMemo(
    () => !!quill?.scroll.query('comment'),
    [quill],
  );

  const lastCodeBlockSyntax = useAppSelector(
    (state) => state.ui.lastCodeBlockSyntax,
  );

  const handleFormat = (format: string, value: unknown) =>
    quill?.format(format, value, Quill.sources.USER);
  const toggleFormat = (name: string) => () =>
    handleFormat(name, !formats[name]);

  return (
    <>
      {!formats.table && (
        <ToolbarButtonGroup>
          <ToolbarObjectButton
            disabled={inTitleline}
            options={objectButtonOptions}
          />
        </ToolbarButtonGroup>
      )}
      <ToolbarButtonGroup>
        <ToolbarButton
          icon={boldIcon}
          active={!!formats.bold}
          disabled={inTitleline || !!formats.header}
          tooltip={<ShortcutTooltip title="Bold" shortcut="Ctrl + b" />}
          testId="editToolbarBoldButton"
          onClick={toggleFormat('bold')}
        />
        <ToolbarButton
          icon={italicIcon}
          active={!!formats.italic}
          disabled={inTitleline}
          tooltip={<ShortcutTooltip title="Italic" shortcut="Ctrl + i" />}
          onClick={toggleFormat('italic')}
        />
        <ToolbarButton
          icon={strikethroughIcon}
          active={!!formats.strike}
          disabled={inTitleline}
          tooltip={
            <ShortcutTooltip title="Strikethrough" shortcut="Ctrl + Alt + x" />
          }
          onClick={toggleFormat('strike')}
        />
        {isSupportingInlineCode && (
          <ToolbarButton
            icon={codeIcon}
            active={!!formats.code}
            disabled={inTitleline || (multiline && !isSupportingCodeEmbed)}
            tooltip={<ShortcutTooltip title="Code" />}
            onClick={() => {
              if (!quill) return;

              if (!multiline) {
                handleFormat('code', !formats.code);
                return;
              }

              if (isSupportingCodeEmbed) {
                const range = quill.getSelection(true);
                convertSelectionToCodeEmbed(
                  quill,
                  range.index,
                  range.length,
                  lastCodeBlockSyntax,
                );
              }
            }}
            testId="editToolbarCodeButton"
          />
        )}
        <ToolbarButton
          icon={linkIcon}
          disabled={inTitleline || multiline}
          active={!!formats.link}
          tooltip={<ShortcutTooltip title="Link" shortcut="Ctrl + k" />}
          testId="editToolbarLinkButton"
          onClick={onLinkSelected}
        />
      </ToolbarButtonGroup>
      {quill && (
        <ToolbarButtonGroup>
          <ToolbarHighlightButton
            quill={quill}
            value={formats.highlight}
            disabled={inTitleline}
            onChange={(value) => handleFormat('highlight', value)}
          />
        </ToolbarButtonGroup>
      )}
      {isSupportingComments && isSupportingAnnotations && (
        <ToolbarButtonGroup>
          <ToolbarCommentButton onClick={closeToolbar} />
          <ToolbarEditModeOverflowMenuButton closeToolbar={closeToolbar} />
        </ToolbarButtonGroup>
      )}
      {isSupportingComments && !isSupportingAnnotations && (
        <ToolbarCommentButtonGroup onCommentButtonClick={closeToolbar} />
      )}
    </>
  );
};

export default EditModeToolbar;
